/**
 * `comparison.build_quotation` is idempotent (`lib/compare.ts`'s `buildQuotationOutcome`) — an
 * existing quotation is REUSED, not rebuilt from the allocation the purchaser just saved. Left
 * to a toast, this mis-bills the customer silently: the toast reads for 2.6s while `navigate`
 * pulls the purchaser onto a quotation screen that looks freshly built but still prices the
 * FIRST split ever saved. So this renders instead — persistent, next to the button that
 * triggered it, and it does not go away until the purchaser acts or changes the allocation
 * (`ComparePage` clears it on every allocation edit).
 */

import { Badge } from '../../components/Badge'
import { Button } from '../../components/ui'

export function QuotationReuseWarning({
  quotation,
  changes,
  repricing,
  onReprice,
  onOpenAnyway,
}: {
  quotation: string
  /** How many of the sent quotation's lines the new allocation would change — 0 when none. */
  changes: number
  repricing: boolean
  /** Write the changed lines and go to the revision page to issue the round. */
  onReprice: () => void
  onOpenAnyway: () => void
}) {
  return (
    <div
      className="banner"
      style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)', marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
      role="alert"
    >
      <span>
        <Badge tone="warn">Sent</Badge> ⚠ <b>{quotation} has already gone to the customer.</b> The allocation is saved, but a
        sent quotation is not rewritten — changing what they were quoted is a <b>revision</b>.
        {changes > 0 ? (
          <>
            {' '}
            This split changes <b>{changes} line{changes === 1 ? '' : 's'}</b> (new supplier or cost).
          </>
        ) : (
          <> This split changes no line’s supplier or cost.</>
        )}
      </span>
      {changes > 0 ? (
        <Button variant="primary" size="sm" disabled={repricing} onClick={onReprice}>
          {repricing ? 'Re-pricing…' : `Re-price ${changes} line${changes === 1 ? '' : 's'} as a revision →`}
        </Button>
      ) : null}
      <span className="linkish" onClick={onOpenAnyway}>
        Open the quotation →
      </span>
    </div>
  )
}
